"use client"

import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { 
	IoCheckmarkCircle,
	IoWarning,
	IoInformationCircle,
	IoCloseCircle 
} from "react-icons/io5"

import { cn } from "@/lib/utils"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Viewport>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Viewport
		ref={ref}
		className={cn(
			"fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:top-auto sm:right-0 sm:bottom-0 sm:flex-col md:max-w-[420px]",
			className,
		)}
		{...props}
	/>
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
	"group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-lg border p-4 pr-8 shadow-lg transition-all duration-300 ease-in-out data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full hover:shadow-xl",
	{
		variants: {
			variant: {
				default:
					"border-border/30 bg-background/95 text-foreground backdrop-blur-sm",
				destructive:
					"destructive group border-destructive/30 bg-destructive/95 text-destructive-foreground backdrop-blur-sm",
				success:
					"border-green-500/30 bg-green-500/95 text-white backdrop-blur-sm",
				warning:
					"border-yellow-500/30 bg-yellow-500/95 text-white backdrop-blur-sm",
				info: "border-blue-500/30 bg-blue-500/95 text-white backdrop-blur-sm",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
)

const Toast = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
		VariantProps<typeof toastVariants>
>(({ className, variant, children, ...props }, ref) => {
	const icon = React.useMemo(() => {
		switch (variant) {
			case "success":
				return <IoCheckmarkCircle className="h-5 w-5 text-white" />
			case "warning":
				return <IoWarning className="h-5 w-5 text-white" />
			case "info":
				return <IoInformationCircle className="h-5 w-5 text-white" />
			case "destructive":
				return <IoCloseCircle className="h-5 w-5 text-white" />
			default:
				return null
		}
	}, [variant])

	return (
		<ToastPrimitives.Root
			ref={ref}
			className={cn(toastVariants({ variant }), className)}
			{...props}
		>
			{icon && <span className="mt-0.5 shrink-0">{icon}</span>}
			<div className="flex-1 gap-1">{children}</div>
		</ToastPrimitives.Root>
	)
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Action>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Action
		ref={ref}
		className={cn(
			"inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 font-medium text-sm ring-offset-background transition-colors duration-150 hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-destructive/30 group-[.info]:border-blue-500/30 group-[.success]:border-green-500/30 group-[.warning]:border-yellow-500/30 group-[.destructive]:focus:ring-destructive group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.info]:hover:bg-blue-500 group-[.success]:hover:bg-green-500 group-[.warning]:hover:bg-yellow-500 group-[.destructive]:hover:text-destructive-foreground group-[.info]:hover:text-white group-[.success]:hover:text-white group-[.warning]:hover:text-white",
			className,
		)}
		{...props}
	/>
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Close>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Close
		ref={ref}
		className={cn(
			"absolute top-1 right-1 rounded-md p-1.5 text-foreground/50 opacity-0 transition-all duration-150 hover:scale-110 hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.info]:text-blue-100 group-[.success]:text-green-100 group-[.warning]:text-yellow-100 group-[.destructive]:hover:text-red-50 group-[.info]:hover:text-blue-50 group-[.success]:hover:text-green-50 group-[.warning]:hover:text-yellow-50",
			className,
		)}
		toast-close=""
		{...props}
	>
		<X className="h-4 w-4" />
	</ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Title>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Title
		ref={ref}
		className={cn("font-semibold text-sm leading-none tracking-tight", className)}
		{...props}
	/>
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Description>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Description
		ref={ref}
		className={cn("text-sm leading-relaxed opacity-90", className)}
		{...props}
	/>
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
	type ToastProps,
	type ToastActionElement,
	ToastProvider,
	ToastViewport,
	Toast,
	ToastTitle,
	ToastDescription,
	ToastClose,
	ToastAction,
}
